import API from "./axios";

// Phase 36da.99.socol — build the query string of SO-PDF visibility flags.
// Only emits a flag when explicitly false (true is the server default).
function soPdfColsQs(cols) {
  if (!cols) return "";
  const map = {
    showCustomer: cols.customer, showAddress: cols.address, showMobile: cols.mobile,
    showSchool: cols.school, showImage: cols.image, showBarcode: cols.barcode, showNotes: cols.notes,
  };
  const parts = Object.entries(map)
    .filter(([, v]) => v === false)
    .map(([k]) => `${k}=false`);
  return parts.length ? `?${parts.join("&")}` : "";
}

const saleOrderApi = {
  getAll:         ()            => API.get("/sale-orders"),
  /** Phase 5 — paged register. params: { page, size, sortBy, sortDir, q, status, customerId, isSample } */
  paged:          (params)      => API.get("/sale-orders/paged", { params }),
  // Phase 36da.99.sogt — page summary: confirmed SO count + Scholar/L1 total.
  // params: { q, isSample, from, to } — tracks the current search/filters.
  confirmedScholarSummary: (params) => API.get("/sale-orders/summary/confirmed-scholar", { params }),
  getById:        (id)          => API.get(`/sale-orders/${id}`),
  // Phase 36da.99.soedit — can this SO be fully edited? → { fullEditable, status, blockers[] }
  editability:    (id)          => API.get(`/sale-orders/${id}/editability`),
  // Phase 36da.99.sizeremap — old size → new size (+ optional qty), propagated downstream.
  remapSize:      (id, soItemId, fromItemSizeId, toItemSizeId, newQty) =>
    API.post(`/sale-orders/${id}/remap-size?soItemId=${soItemId}&fromItemSizeId=${fromItemSizeId}&toItemSizeId=${toItemSizeId}${newQty != null ? `&newQty=${newQty}` : ""}`),
  // Phase 36da.99.sorate — connect a different quotation of the same customer.
  customerQuotations: (id) => API.get(`/sale-orders/${id}/customer-quotations`),
  relinkQuotation:    (id, quotationId) =>
    API.post(`/sale-orders/${id}/relink-quotation?quotationId=${quotationId}`),
  // Phase 36da.99.mqr — pull rates from multiple quotations by barcode
  // (no official re-link).  quotationIds: array of numbers.
  pullRates:          (id, quotationIds) =>
    API.post(`/sale-orders/${id}/pull-rates?` +
      quotationIds.map(q => `quotationIds=${encodeURIComponent(q)}`).join("&")),
  getByStatus:    (status)      => API.get(`/sale-orders/status/${status}`),
  getByCustomer:  (customerId)  => API.get(`/sale-orders/customer/${customerId}`),
  getByDateRange: (from, to)    => API.get(`/sale-orders/date-range?from=${from}&to=${to}`),
  search:         (keyword)     => API.get(`/sale-orders/search?keyword=${encodeURIComponent(keyword)}`),
  /** Phase 5 — predict next SO number for create-form prefill. Idempotent. */
  peekNumber:     ()            => API.get("/sale-orders/peek-number"),
  create:         (data)        => API.post("/sale-orders", data),
  update:         (id, data)    => API.put(`/sale-orders/${id}`, data),
  // Phase 36da.99.df — limited post-confirm edit (salesperson, customer
  // PO no, order date, SO number).
  updateMeta:     (id, data)    => API.patch(`/sale-orders/${id}/update-meta`, data),
  confirm:        (id)          => API.patch(`/sale-orders/${id}/confirm`),
  cancel:         (id)          => API.patch(`/sale-orders/${id}/cancel`),
  delete:         (id)          => API.delete(`/sale-orders/${id}`),
  uploadItemImage:(soId, itemId, formData) =>
    API.post(`/sale-orders/${soId}/items/${itemId}/image`, formData, {
      headers: { "Content-Type": undefined },
    }),
  downloadProformaInvoice: (id, cols) =>
    API.get(`/sale-orders/${id}/proforma-invoice${soPdfColsQs(cols)}`, { responseType: "arraybuffer" }),


  // Tech Pack
  uploadTechPack: (soId, itemId, formData) =>
    API.post(`/sale-orders/${soId}/items/${itemId}/tech-pack`, formData, {
      headers: { "Content-Type": undefined },
    }),
  downloadTechPack: (soId, itemId) =>
    API.get(`/sale-orders/${soId}/items/${itemId}/tech-pack`, { responseType: "arraybuffer" }),
  deleteTechPack: (soId, itemId) =>
    API.delete(`/sale-orders/${soId}/items/${itemId}/tech-pack`),

  // Sample tracking
  getSamples:          ()           => API.get("/sale-orders/samples"),
  markDispatched:      (soId, date) => API.patch(`/sale-orders/${soId}/sample/dispatch`, { dispatchDate: date }),
  markConverted:       (soId, bulkSoId) => API.patch(`/sale-orders/${soId}/sample/convert`, { bulkSoId }),
  markRejected:        (soId, reasonId, notes) => API.patch(`/sale-orders/${soId}/sample/reject`, { reasonId, notes }),
  getSoTracking:       ()           => API.get("/reports/so-tracking"),
  getSoTrackingDetail: (soId)       => API.get(`/reports/so-tracking/${soId}`),
  getProductTechPacks: (productId) =>
    API.get(`/sale-orders/tech-packs/product/${productId}`),

  // ── Export endpoints ──────────────────────────────────────────────────
  // Phase 36da.79 — q (search) param so exports match the on-screen filter.
  exportSoTrackingExcel: (from, to, tab, q) => API.get("/reports/so-tracking/export/excel", {
    params: { from: from || undefined, to: to || undefined, tab: tab || "ALL", q: q || undefined },
    responseType: "blob",
  }),
  exportSoDetailExcel: (soId) => API.get(`/reports/so-tracking/${soId}/export/excel`, {
    responseType: "blob",
  }),
  exportSoTrackingPdf: (from, to, tab, q) => API.get("/reports/so-tracking/export/pdf", {
    params: { from: from || undefined, to: to || undefined, tab: tab || "ALL", q: q || undefined },
    responseType: "blob",
  }),
  exportSoDetailPdf: (soId) => API.get(`/reports/so-tracking/${soId}/export/pdf`, { responseType: "blob" }),

  // Phase 33 — Cloth substitution audit per SO
  getClothUsage: (soId) => API.get(`/sale-orders/${soId}/cloth-usage`),

  // Phase 36da.99.bi — Per-item shop-floor instructions (English + Marathi).
  // Editable once the SO is CONFIRMED; rendered downstream on cutting,
  // contractor and dept issue prints.
  getInstructions:    (soId)       => API.get(`/sale-orders/${soId}/instructions`),
  updateInstructions: (soId, data) => API.put(`/sale-orders/${soId}/instructions`, data),
  // Phase 36da.99.csr — original SO rate vs billed (DC "Change Sale Rate") override.
  saleRateComparison: (soId)       => API.get(`/sale-orders/${soId}/sale-rate-comparison`),
  // Location split — how much of each line and size each location makes
  // (e.g. 5,000 of a 10,000 order at Sangola). The main location makes the rest.
  getLocationSplit:  (soId)       => API.get(`/sale-orders/${soId}/location-split`),
  saveLocationSplit: (soId, data) => API.put(`/sale-orders/${soId}/location-split`, data),
};

export const soReportApi = {
  byCustomerExcel: (customerId, from, to, level) => API.get("/sale-orders/reports/by-customer", {
    params: { customerId, from, to, level }, responseType: "blob",
  }),
  bySchoolExcel: (schoolId, from, to, level) => API.get("/sale-orders/reports/by-school", {
    params: { schoolId, from, to, level }, responseType: "blob",
  }),
  // Phase 36da.82 — Register-level exports driven by the page's own
  // search + date filter (no popup, no customer/school selection).
  // Phase 36da.99.sorptf — also pass the tab filter (isSample + excludeCancelled).
  registerExcel: (q, from, to, level, isSample, excludeCancelled) => API.get("/sale-orders/reports/register/excel", {
    params: { q: q || undefined, from: from || undefined, to: to || undefined, level, isSample, excludeCancelled },
    responseType: "blob",
  }),
  registerPdf: (q, from, to, level, isSample, excludeCancelled) => API.get("/sale-orders/reports/register/pdf", {
    params: { q: q || undefined, from: from || undefined, to: to || undefined, level, isSample, excludeCancelled },
    responseType: "blob",
  }),
};

export default saleOrderApi;